import { useRoutes, matchRoutes } from "react-router-dom";
import React, { useEffect, useState } from "react";
// project import
import LoginRoutes from "./LoginRoutes";
import MainRoutes from "./MainRoutes";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { isExpired, decodeToken } from "react-jwt";
import { LOGIN_AUTH, LOGOUT_AUTH } from "store/reducers/auth";

// ==============================|| ROUTING RENDER ||============================== //

export default function ThemeRoutes() {
  const userModel = useSelector((state) => state.auth.userModel);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    checkLogin();
  }, [userModel.isLogin, location.pathname]);

  const [access_list_data, setaccess_list_data] = useState([]);
  const [refresh, set_refresh] = useState([]);

  // Safely read the cached access list. Old code blindly JSON.parse'd
  // whatever was in localStorage, which for users with no backend `data`
  // key ended up as the literal string "undefined" and threw — leaving
  // the user on a blank page.
  const readStoredAccessList = () => {
    const raw = localStorage.getItem("access_list_data");
    if (!raw || raw === "undefined" || raw === "null") return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (userModel.isLogin) {
      const storedData = localStorage.getItem("access_list_data");
      if (storedData) {
        setaccess_list_data(readStoredAccessList());
      } else {
        set_refresh(!refresh);
      }
    }
  }, [refresh, userModel.isLogin]);

  // Recovery: if checkLogin parked us at /no-access (because the access
  // list hadn't been written to localStorage yet at navigation time) but
  // the list has since loaded with real permissions, send the user to
  // their proper landing page instead of leaving them stuck.
  useEffect(() => {
    if (
      userModel.isLogin &&
      location.pathname === "/no-access" &&
      Array.isArray(access_list_data) &&
      access_list_data.length > 0
    ) {
      navigate(pickLandingPath(access_list_data), { replace: true });
    }
  }, [access_list_data, location.pathname, userModel.isLogin]);

  // Pick the first route the user actually has read_access to. Hard-coding
  // navigate("/tickets") meant a user without Tickets access landed on an
  // unmatched route → blank page (or the catchall NoAccessPage with no
  // chrome on the first render). This mirrors the conditional routes in
  // MainRoutes.js — keep the two lists in sync if new modules are added.
  const pickLandingPath = (accessList) => {
    if (!Array.isArray(accessList) || accessList.length === 0) {
      return "/no-access";
    }
    const has = (moduleName) =>
      Boolean(
        accessList.find(
          (item) => item.module_name === moduleName && item.read_access,
        ),
      );
    if (has("Tickets")) return "/tickets";
    if (has("Dashboard")) return "/dashboard";
    if (has("Assets")) return "/asset";
    if (has("Audit")) return "/audit";
    if (has("Checklist")) return "/checklist";
    if (has("Spares")) return "/spare-management";
    if (has("Spare Analytics")) return "/spare-analytics";
    if (has("Visitor Details")) return "/visitor-details";
    if (has("Report")) return "/report";
    return "/no-access";
  };

  function checkLogin() {
    const token = localStorage.getItem("Token");
    const isMyTokenExpired = isExpired(token);

    if (!userModel.isLogin) {
      if (
        (location.pathname !== "/login" || location.pathname !== "/") &&
        isMyTokenExpired
      ) {
        const loginRoutes = [LoginRoutes()];
        const matchedRoutes = matchRoutes(loginRoutes, location.pathname);
        matchedRoutes
          ? navigate({
              pathname: location.pathname,
              search: location.search,
              hash: location.hash,
            })
          : navigate("/login");
        localStorage.setItem("lang", "en");
      }
    } else if (isMyTokenExpired && userModel.isLogin) {
      dispatch(LOGOUT_AUTH());
      navigate("/login");
    } else if (
      (location.pathname === "/login" || location.pathname === "/") &&
      !isMyTokenExpired
    ) {
      dispatch(LOGIN_AUTH());
      // Land on the first module the user can actually see — falls back
      // to /no-access if their role grants zero read permissions.
      navigate(pickLandingPath(readStoredAccessList()));
    } else {
      dispatch(LOGIN_AUTH());
    }
  }

  return useRoutes([
    LoginRoutes(),
    userModel.isLogin && MainRoutes(access_list_data),
  ]);
}
